import { useEffect, useState } from "react";
import IListAllSolarPlantsRequestDTO from "../../../../shared/oversight-core/dtos/request-dtos/list-all-solar-plants-response-dto";
import { EPlantStatus } from "../../../../shared/oversight-core/enums/plant-status";
import { ESolarPlantSize } from "../../../../shared/oversight-core/enums/solar-plant-size";
import FilteringSidePanel from "../../../../shared/oversight-core/shared-components/filtering-side-panel/filtering-side-panel";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";

interface IProps {
  show: boolean;
  onClose: () => void;
  updateFilters: (
    generationServiceProviderId: string,
    solarPlantSize?: ESolarPlantSize,
    plantStatus?: EPlantStatus,
  ) => void;
  filters: IListAllSolarPlantsRequestDTO;
  solarBrandList: Option[];
}

const solarBrandDefaultOption: Option = {
  value: "",
  label: "Select a solar brand",
};

const solarSizeDefaultOption: Option = {
  value: "",
  label: "Select a size",
};

const solarPlatStatusDefaultOption: Option = {
  value: "",
  label: "All Sites",
};

const sizeOption: Option[] = [
  { value: ESolarPlantSize.UNSPECIFIED, label: "Capacity not set" },
  { value: ESolarPlantSize.SMALL, label: "0 - 10KW" },
  { value: ESolarPlantSize.MEDIUM, label: "10kW - 50kW" },
  { value: ESolarPlantSize.LARGE, label: "50kW - 100kW" },
  { value: ESolarPlantSize.EXTRA_LARGE, label: "Greater than 100kW" },
];

const plantStatus: Option[] = [
  { value: EPlantStatus.ACTIVE, label: "Active Sites" },
  { value: EPlantStatus.WAITING, label: "Standby Sites" },
  { value: EPlantStatus.OFFLINE, label: "Offline Sites" },
  { value: EPlantStatus.FAULT, label: "Error Sites" },
  { value: EPlantStatus.DISABLED, label: "Disabled" },
];

const findOption = (options: Option[], value: string | undefined) =>
  value ? options.find((option) => option.value === value) : undefined;

const SitesFilteringSidePanel = (props: IProps) => {
  const { show, filters, onClose, updateFilters, solarBrandList } = props;

  const [selectedSolarBrand, setSelectedSolarBrand] = useState<Option>({
    ...solarBrandDefaultOption,
  });
  const [selectedSize, setSelectedSize] = useState<Option>({
    ...solarSizeDefaultOption,
  });
  const [selectedStatus, setSelectedStatus] = useState<Option>({
    ...solarPlatStatusDefaultOption,
  });

  // The form shows what is applied each time the drawer opens, so a choice that was never applied
  // does not come back after the drawer is closed.
  useEffect(() => {
    if (!show) {
      return;
    }

    setSelectedSolarBrand(
      findOption(solarBrandList, filters.generationServiceProviderId) ?? {
        ...solarBrandDefaultOption,
      },
    );
    setSelectedSize(
      findOption(sizeOption, filters.solarPlantSize) ?? {
        ...solarSizeDefaultOption,
      },
    );
    setSelectedStatus(
      findOption(plantStatus, filters.plantStatus) ?? {
        ...solarPlatStatusDefaultOption,
      },
    );
  }, [
    show,
    filters.generationServiceProviderId,
    filters.solarPlantSize,
    filters.plantStatus,
    solarBrandList,
  ]);

  return (
    <FilteringSidePanel
      isOpen={show}
      onClose={onClose}
      onFilter={() => {
        updateFilters(
          selectedSolarBrand.value,
          selectedSize.value as ESolarPlantSize,
          selectedStatus.value as EPlantStatus,
        );
        onClose();
      }}
      onClearAll={() => {
        // Clears the form and the applied filters back to the defaults, and closes the drawer.
        setSelectedSolarBrand({ ...solarBrandDefaultOption });
        setSelectedSize({ ...solarSizeDefaultOption });
        setSelectedStatus({ ...solarPlatStatusDefaultOption });
        updateFilters("", undefined, undefined);
        onClose();
      }}
    >
      <>
        <AppSelect
          className="text-light font-size-14"
          selectedValue={selectedSolarBrand}
          label="Solar Brand"
          options={solarBrandList}
          onChangeOption={(selectedOption) => {
            setSelectedSolarBrand(selectedOption);
          }}
          isRequired={false}
          labelFontSize="font-size-14"
        />
        <AppSelect
          className="text-light font-size-14 mt-4"
          selectedValue={selectedSize}
          label="Size"
          options={sizeOption}
          onChangeOption={(selectedOption) => {
            setSelectedSize(selectedOption);
          }}
          errors={Error}
          isRequired={false}
          labelFontSize="font-size-14"
        />
        <AppSelect
          className="text-light font-size-14 mt-4"
          selectedValue={selectedStatus}
          label="Status"
          options={plantStatus}
          onChangeOption={(selectedOption) => {
            setSelectedStatus(selectedOption);
          }}
          errors={Error}
          isRequired={false}
          labelFontSize="font-size-14"
        />
      </>
    </FilteringSidePanel>
  );
};

export default SitesFilteringSidePanel;
